// The phone's horizontal rails advance on their own.
//
//   const rail = useRef(null);
//   useAutoRail(rail);
//   <ul className="ld-rail" ref={rail}>...</ul>
//
// Sits still for 2s, smooth-scrolls to the next snap item, and wraps to the
// first at the end. Pauses while the mouse is over it, focus is inside it, a
// finger is on it (and for one interval after), the tab is hidden, or less than
// 30% of it is on screen. Does nothing under reduced motion, with fewer than
// two items, or when the overflow is 4px or less, which is why it switches
// itself off on the desktop grids. Pass `trigger` if the items change later.

import { useEffect } from "react";
import { prefersReducedMotion } from "./env";

const INTERVAL = 2000;

// In RTL the rail's start is its right edge and scrollLeft runs negative, so
// items are compared by how far their start sits past the rail's start.
const advance = (rail) => {
    const items = Array.from(rail.children);
    const remaining = rail.scrollWidth - rail.clientWidth - Math.abs(rail.scrollLeft);
    if (remaining <= 4) {
        rail.scrollTo({ left: 0, behavior: "smooth" });
        return;
    }
    const pad = parseFloat(getComputedStyle(rail).scrollPaddingInlineStart) || 0;
    const start = rail.getBoundingClientRect().right - pad;
    const next = items.find((item) => start - item.getBoundingClientRect().right > 4);
    if (!next) {
        rail.scrollTo({ left: 0, behavior: "smooth" });
        return;
    }
    rail.scrollBy({ left: next.getBoundingClientRect().right - start, behavior: "smooth" });
};

export default function useAutoRail(ref, { interval = INTERVAL, trigger } = {}) {
    useEffect(() => {
        const rail = ref?.current;
        if (!rail || prefersReducedMotion()) return undefined;

        let over = false;
        let focused = false;
        let touching = false;
        let touchedAt = 0;
        let visible = false;

        const tick = () => {
            if (over || focused || touching || !visible || document.hidden) return;
            if (performance.now() - touchedAt < interval) return;
            if (rail.children.length < 2) return;
            if (rail.scrollWidth - rail.clientWidth <= 4) return;
            advance(rail);
        };
        const timer = setInterval(tick, interval);

        const io = new IntersectionObserver((entries) => {
            visible = entries[entries.length - 1].isIntersecting;
        }, { threshold: 0.3 });
        io.observe(rail);

        const onEnter = () => { over = true; };
        const onLeave = () => { over = false; };
        const onFocusIn = (e) => { focused = e.target.matches?.(":focus-visible") ?? true; };
        const onFocusOut = () => { focused = false; };
        const onTouchStart = () => { touching = true; };
        const onTouchEnd = () => { touching = false; touchedAt = performance.now(); };

        rail.addEventListener("mouseenter", onEnter);
        rail.addEventListener("mouseleave", onLeave);
        rail.addEventListener("focusin", onFocusIn);
        rail.addEventListener("focusout", onFocusOut);
        rail.addEventListener("touchstart", onTouchStart, { passive: true });
        rail.addEventListener("touchend", onTouchEnd, { passive: true });
        rail.addEventListener("touchcancel", onTouchEnd, { passive: true });

        return () => {
            clearInterval(timer);
            io.disconnect();
            rail.removeEventListener("mouseenter", onEnter);
            rail.removeEventListener("mouseleave", onLeave);
            rail.removeEventListener("focusin", onFocusIn);
            rail.removeEventListener("focusout", onFocusOut);
            rail.removeEventListener("touchstart", onTouchStart);
            rail.removeEventListener("touchend", onTouchEnd);
            rail.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [ref, interval, trigger]);
}
